// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useRef, useCallback } from 'react'
import { Buffer } from 'buffer'
import { multiParse } from 'utils'
import { removeFileExt } from 'utils/stringFunctions'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaAxios from './useInnovaAxios'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { QUERY_KEYS } from '../queryKeys'
import { saveOrgIconsToDB, loadOrgIconsFromDB } from 'utils/indexedDB'

const getIconCacheKey = (icon) => {
  const fileName = icon?.fileName || icon?.filename || icon?.filePath?.split('%2F').pop() || ''
  return removeFileExt(fileName).toLowerCase()
}

const isNewerLastModified = (localLastModified, remoteLastModified) => {
  if (!localLastModified) return true
  if (!remoteLastModified) return false

  const localTime = new Date(localLastModified).getTime()
  const remoteTime = new Date(remoteLastModified).getTime()

  if (Number.isNaN(localTime)) return true
  if (Number.isNaN(remoteTime)) return false

  return localTime < remoteTime
}

const useOrgIcons = () => {
  const _isMounted = useRef(false)
  const { userRole } = useInnovaAuth()
  const orgTheme = userRole?.userPrefs?.organizationTheme

  const getAllOrgIcons = useInnovaAxios({
    url: '/admin/getOrgIcons',
  })

  const getAllOrgIconMetadata = useInnovaAxios({
    url: '/admin/getOrgIconMetadata',
  })

  const { data: orgIcons, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.orgIconsKey],
    queryFn: async () => fetchOrgIcons(),
    staleTime: Infinity,
  })

  const queryClient = useQueryClient()

  // this useEffect is located before the initial useEffect to ensure it doesn't run
  // during the hook initialization, invoking multiple fetches.
  useEffect(() => {
    if (!_isMounted.current) return
    refresh(false)
  }, [userRole?.organization]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    _isMounted.current = true
    const cachedOrgIcons = queryClient.getQueryData([QUERY_KEYS.orgIconsKey])
    if (Array.isArray(cachedOrgIcons) && cachedOrgIcons.length > 0) {
      refresh(false)
    }

    return () => {
      _isMounted.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getOrgIconMetadata = async () => {
    const res = await getAllOrgIconMetadata()
    return Array.isArray(res?.data) ? res.data : []
  }

  const getRefreshMetadata = async (localOrgIcons) => {
    if (!Array.isArray(localOrgIcons) || localOrgIcons.length === 0) {
      return []
    }

    const metadata = await getOrgIconMetadata()
    if (!metadata.length) {
      return null
    }

    const localIconMap = new Map(localOrgIcons.map((icon) => [getIconCacheKey(icon), icon]))
    const hasMissingLastModified = localOrgIcons.some((icon) => !icon.lastModified)
    if (hasMissingLastModified) {
      return metadata
    }

    const hasStaleIcon = metadata.some((remoteIcon) => {
      const localIcon = localIconMap.get(getIconCacheKey(remoteIcon))
      return !localIcon || isNewerLastModified(localIcon.lastModified, remoteIcon.lastModified)
    })

    return hasStaleIcon ? metadata : null
  }

  const applyOrgIconMetadata = (icons, metadata) => {
    if (!Array.isArray(metadata) || metadata.length === 0) {
      return icons
    }

    const metadataMap = new Map(metadata.map((icon) => [getIconCacheKey(icon), icon]))

    return icons.map((icon) => {
      const iconMetadata = metadataMap.get(getIconCacheKey(icon))
      return iconMetadata ? { ...icon, ...iconMetadata, filename: icon.filename } : icon
    })
  }

  const fetchOrgIcons = async (forceUpdate = false) => {
    let metadata = []

    if (!forceUpdate) {
      let localOrgIcons = []

      try {
        localOrgIcons = await loadOrgIconsFromDB() // try to load from IndexedDB
      } catch (error) {
        console.warn('Error loading org icons from IndexedDB:', error)
      }

      if (Array.isArray(localOrgIcons) && localOrgIcons.length > 0) {
        try {
          const refreshMetadata = await getRefreshMetadata(localOrgIcons)
          if (!refreshMetadata) {
            return localOrgIcons // return the cached icons
          }
          metadata = refreshMetadata
        } catch (error) {
          console.warn('Error loading org icon cache metadata:', error)
          return localOrgIcons
        }
      }
    } else {
      try {
        metadata = await getOrgIconMetadata()
      } catch (error) {
        console.warn('Error loading org icon metadata:', error)
      }
    }

    const res = await getAllOrgIcons()

    if (!res.data) {
      throw new Error('Failed to fetch org icon data')
    }

    const mfpBoundary = res.data.substring(2, res.data.search('Content')).trim()
    let parts = multiParse(Buffer.from(res.data), mfpBoundary)
    let icons = parts.map((part) => {
      let iconObj = { ...part }
      iconObj.data = 'data:image/*;base64,' + part.data
      iconObj.filename = removeFileExt(part.filename)
      return iconObj
    })
    icons = applyOrgIconMetadata(icons, metadata)

    try {
      await saveOrgIconsToDB(icons) // save to IndexedDB
    } catch (error) {
      console.warn('Error saving org icons to IndexedDB:', error)
    }
    return icons
  }

  const refresh = async (forceUpdate = true) => {
    const icons = await fetchOrgIcons(forceUpdate)
    if (icons && Array.isArray(icons) && _isMounted.current) {
      queryClient.setQueryData([QUERY_KEYS.orgIconsKey], icons)
    }
  }

  const getCompanyIcon = useCallback(
    (orgName, useDefault = false) => {
      if (orgIcons && Array.isArray(orgIcons)) {
        const icon = orgIcons.find((item) => item.filename === orgName.toLowerCase())
        if (icon) {
          return icon.data
        } else {
          if (useDefault) {
            const defaultIcon = orgIcons.find((item) => item.filename === 'innova')
            if (defaultIcon) {
              return defaultIcon.data
            }
          }
        }
      }
      return ''
    },
    [orgIcons],
  )

  const getCurrentOrgIcon = () => {
    if (!userRole) return ''
    if (!userRole.hasOwnProperty('organization')) return ''
    if (!userRole.organization) return ''

    if (orgIcons && Array.isArray(orgIcons)) {
      let icon = orgIcons.find((item) => item.filename === userRole.organization.toLowerCase())

      if (icon) {
        return icon.data
      }
    }
    return ''
  }

  const getCurrentOrgIconFilePath = () => {
    if (!userRole) return ''
    if (!userRole.hasOwnProperty('organization')) return ''
    if (!userRole.organization) return ''

    if (orgIcons && Array.isArray(orgIcons)) {
      let icon = orgIcons.find((item) => item.filename === userRole.organization.toLowerCase())
      if (icon) {
        const filePath = icon.filePath
        const parts = filePath.split('%2F')
        const result = parts.length > 1 ? parts[1] : filePath
        return result
      }
    }
    return ''
  }

  const currentOrgLogo = useMemo(() => {
    if (!userRole) return ''
    if (!userRole.hasOwnProperty('organization') || !userRole.organization) return ''
    if (!userRole.organization) return ''

    if (orgIcons && Array.isArray(orgIcons)) {
      const orgLogoName = userRole.organization.toLowerCase() + '-logo'
      let icon = orgIcons.find((item) => item.filename === orgLogoName)
      if (icon) {
        return icon.data
      }
    }
    return ''
  }, [orgIcons, userRole])

  const displayInnovaLogo = () => {
    if (orgTheme?.useSecondaryLogo) {
      return true
    }

    return false
  }

  const getCompanyLogo = (orgName, useDefault = false) => {
    if (orgIcons && Array.isArray(orgIcons)) {
      const orgLogoName = orgName.toLowerCase() + '-logo'
      const icon = orgIcons.find((item) => item.filename === orgLogoName)
      if (icon) {
        return icon.data
      }

      if (useDefault) {
        const defaultIcon = orgIcons.find((item) => item.filename === 'innova-logo')
        if (defaultIcon) {
          return defaultIcon.data
        }
      }
    }
    return ''
  }

  const getOrgIcons = () => {
    if (orgIcons && Array.isArray(orgIcons)) {
      return orgIcons
    }

    return []
  }

  return {
    getCompanyIcon,
    getOrgIcons,
    getCurrentOrgIcon,
    getCompanyLogo,
    displayInnovaLogo,
    currentOrgLogo,
    getCurrentOrgIconFilePath,
    refresh,
    isLoading,
    orgIcons,
  }
}

export default useOrgIcons
